import { useMemo } from "react";
import { PatchNoteEntry } from "../state/patch-notes-interfaces";
import CategoryBadge, { dotModifier } from "./CategoryBadge";
import ModuleChip from "./ModuleChip";

/**
 * Groups a release's entries by category and renders one badge + bullet list per group.
 * Shared by WhatsNewModal and the release-history page so both read identically.
 */

// Display order, independent of the order entries happen to appear in patch-notes.json.
const CATEGORY_ORDER = ['new', 'improved', 'fixed'];

function sortKey(category: string): number {
    const index = CATEGORY_ORDER.indexOf(category?.toLowerCase());
    // Unknown categories sort last rather than being dropped.
    return index === -1 ? CATEGORY_ORDER.length : index;
}

interface ReleaseEntryListProps {
    entries: PatchNoteEntry[],
}

export default function ReleaseEntryList({ entries }: ReleaseEntryListProps) {
    const groups = useMemo(() => {
        const byCategory = new Map<string, PatchNoteEntry[]>();

        entries.forEach(entry => {
            // Group on the raw code, not a lowercased copy, so the badge still receives what
            // the API sent.
            const existing = byCategory.get(entry.category);
            if (existing) {
                existing.push(entry);
            } else {
                byCategory.set(entry.category, [entry]);
            }
        });

        return Array.from(byCategory.entries())
            .sort((a, b) => sortKey(a[0]) - sortKey(b[0]));
    }, [entries]);

    return (
        <div className="pn-groups">
            {groups.map(([category, items]) => (
                <div key={category}>
                    <CategoryBadge category={category} />
                    <ul className="pn-group-items">
                        {items.map((entry, index) => (
                            <li key={index} className="pn-item">
                                <span className={`pn-item-dot ${dotModifier(category)}`} />
                                {/* The chip sits inside the text span, not beside it as a third
                                    flex child: inline-block means a long description wraps
                                    underneath the chip rather than into a narrow column beside
                                    it. Matches the mockup, which nests it the same way. */}
                                <span className="pn-item-text">
                                    <ModuleChip module={entry.module} />
                                    {entry.description}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}
